import {
  Group,
  Popover,
  Stack,
  Divider,
  Text,
  noop,
  useMantineTheme,
} from "@mantine/core"
import {
  IconArrowsSort,
  IconCirclePlus,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from "@tabler/icons-react"
import { ActionIcon, Button, DragNDrop, SearchInput, Select } from "@tfg/ui"
import { TFunction } from "i18next"
import _ from "lodash"
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import { SortValueDto } from "~/gen/api"

type SortDirection = "asc" | "desc"

export type Sorts = {
  [key: string]: SortValueDto
}

export interface SortBuilderProps<T> {
  options: {
    label: string
    value: keyof T
  }[]
  sorts: Sorts
  setSorts: Dispatch<SetStateAction<Sorts>>
}

const getSortButtonLabel = (
  sorts: Sorts,
  getLabel: (key: string) => string,
  t: TFunction<"common">,
  SORT_DIRECTIONS: {
    [direction in "asc" | "desc"]: {
      label: string
      icon: ReactNode
    }
  }
): { label: string; icon: ReactNode } => {
  const sortKeys = Object.keys(sorts)

  if (sortKeys.length === 1) {
    const [key, value] = Object.entries(sorts)[0]
    return {
      label: getLabel(key),
      icon: SORT_DIRECTIONS[value.value]?.icon ?? null,
    }
  } else if (sortKeys.length > 1) {
    return {
      label: `${t("sort.title")} (${sortKeys.length})`,
      icon: <IconArrowsSort stroke={1} size={16} />,
    }
  } else {
    return {
      label: t("sort.title"),
      icon: <IconArrowsSort stroke={1} size={16} />,
    }
  }
}

export const SortBuilder = <T extends Record<string, SortValueDto>>({
  options,
}: SortBuilderProps<T>) => {
  const { sorts, setSorts } = useHeaderControlsContext()
  const { t } = useTranslation(["common"])
  const theme = useMantineTheme()
  const SORT_DIRECTIONS: {
    [direction in SortDirection]: {
      label: string
      icon: ReactNode
    }
  } = {
    asc: {
      label: t("sort.ascending"),
      icon: (
        <IconSortAscending
          stroke={theme.other.icon.stroke}
          size={theme.other.icon.size}
        />
      ),
    },
    desc: {
      label: t("sort.descending"),
      icon: (
        <IconSortDescending
          stroke={theme.other.icon.stroke}
          size={theme.other.icon.size}
        />
      ),
    },
  }

  const [search, setSearch] = useState<string>("")
  const getLabel = (key: string) => {
    return options.find((option) => option.value === key)?.label ?? key
  }

  const filteredOptions = useMemo(
    () =>
      _.orderBy(
        options.filter(
          (option) =>
            option.label.toLowerCase().includes(search.toLowerCase()) &&
            !Object.keys(sorts).includes(option.value as string)
        ),
        "label"
      ),
    [search, options, sorts]
  )

  const { label, icon } = getSortButtonLabel(
    sorts,
    getLabel,
    t,
    SORT_DIRECTIONS
  )

  return (
    <Popover position="bottom-start">
      <Popover.Target>
        <Button
          px="xs"
          variant={Object.keys(sorts).length > 0 ? "secondary" : "ghost"}
          rightSection={icon}
          fw={500}
        >
          {label}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <SearchInput
            placeholder={t("sort.search.placeholder")}
            inputValue={search}
            delay={0}
            onOpen={noop}
            onChange={(value) => {
              setSearch(value.toString())
            }}
            size="xs"
          />
          <DragNDrop
            onDragEnd={({ item, destination }) => {
              if (!destination || destination.index === null) return
              setSorts((prev) => {
                const newSorts = { ...prev }

                newSorts[item.id] = {
                  ...newSorts[item.id],
                  rank: destination.index,
                }
                for (const [key, value] of Object.entries(newSorts)) {
                  if (value.rank >= destination.index && key !== item.id) {
                    newSorts[key] = {
                      ...value,
                      rank: value.rank + 1,
                    }
                  }
                }
                return newSorts
              })
            }}
            data={_.orderBy(Object.entries(sorts), (sort) => sort[1].rank).map(
              ([key, value]) => ({
                id: key,
                label: getLabel(key),
                value,
                cta: (
                  <Group>
                    <Select
                      value={value.value}
                      data={Object.keys(SORT_DIRECTIONS).map((key) => ({
                        label: SORT_DIRECTIONS[key as SortDirection].label,
                        value: key,
                      }))}
                      onChange={(val) =>
                        setSorts((prev) => ({
                          ...prev,
                          [key]: {
                            value: val as SortDirection,
                            rank: value.rank,
                          },
                        }))
                      }
                      w={150}
                      comboboxProps={{
                        withinPortal: false,
                      }}
                    />

                    <ActionIcon
                      onClick={() => {
                        setSorts((prev) => {
                          const newSorts = { ...prev }
                          const rank = newSorts[key].rank
                          delete newSorts[key]
                          for (const [key2, value2] of Object.entries(
                            newSorts
                          )) {
                            if (value2.rank > rank) {
                              newSorts[key2] = {
                                ...value2,
                                rank: value2.rank - 1,
                              }
                            }
                          }
                          return newSorts
                        })
                      }}
                      icon={<IconX />}
                    />
                  </Group>
                ),
                itemProps: {
                  bd: 0,
                  m: 0,
                  px: 0,
                  py: 2,
                  display: "flex",
                  flexWrap: "nowrap",
                },
              })
            )}
          />
          {!!Object.keys(sorts).length && <Divider />}
          <Stack gap={theme.spacing.xxs}>
            {filteredOptions.map((option) => {
              return (
                <Group key={option.value as string} justify="space-between">
                  <Text>{option.label}</Text>
                  <ActionIcon
                    onClick={() => {
                      setSorts((prev) => ({
                        ...prev,
                        [option.value as string]: {
                          value: "asc" as SortDirection,
                          rank: Object.keys(sorts).length,
                        },
                      }))
                    }}
                    variant="tertiary"
                    icon={<IconCirclePlus />}
                  />
                </Group>
              )
            })}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
